/**
 * authToken.js — centralized, safe token extraction.
 *
 * Single source of truth for reading the auth token from the browser.
 * Handles all three legacy key variants used across the codebase:
 *   1. Zustand persisted store:   localStorage['eventflow-auth']  -> { state: { token } }
 *   2. Legacy flat key:           localStorage['eventflow_token']
 *   3. Oldest flat key:           localStorage['token']
 *
 * NEVER inline `JSON.parse(localStorage.getItem('eventflow-auth'))` anywhere else —
 * that pattern produces `Bearer undefined` when any layer is missing, breaking auth.
 */

export function getToken() {
  // View-As / preview mode wins — when the admin previews as a student the
  // preview JWT lives only in sessionStorage and must take precedence over
  // the admin's own token, otherwise every shared API call authenticates as
  // the admin and the previewed student sees mismatched data.
  try {
    const preview = sessionStorage.getItem('school_preview_token');
    if (preview && typeof preview === 'string') return preview;
  } catch (_e) { /* sessionStorage unavailable */ }
  // Super-admin role perspective (read-only preview of a lower tier).
  try {
    const raw = sessionStorage.getItem('admin_view_as');
    if (raw) {
      const t = JSON.parse(raw)?.token;
      if (t && typeof t === 'string') return t;
    }
  } catch (_e) { /* ignore malformed preview */ }
  try {
    const raw = localStorage.getItem('eventflow-auth');
    if (raw) {
      const parsed = JSON.parse(raw);
      const t = parsed?.state?.token;
      if (t && typeof t === 'string') return t;
    }
  } catch (_e) {
    /* ignore malformed store */
  }
  const flat = localStorage.getItem('eventflow_token');
  if (flat) return flat;
  // Member portal sign-in keeps its own key; without it pages that read the
  // token through here (e.g. the member giving history) look unauthenticated.
  const member = localStorage.getItem('member_token');
  if (member) return member;
  const legacy = localStorage.getItem('token');
  if (legacy) return legacy;
  return '';
}

export function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Convenience — returns Bearer string or empty. */
export function bearer() {
  const t = getToken();
  return t ? `Bearer ${t}` : '';
}
